import React, {FC, useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {InboxGraph} from './inboxGraph';
import type {InboxGraphProps} from './inboxGraphTypes';

const BIG_GRAPH_PADDING = 72;

/*
 * Style.
 */

const StyledSmallGraphWrapper = styled.div<{$isExpandable: boolean}>`
  border-radius: 12px;
  background-color: ${(p) => p.theme.greys.shade10};
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  padding: ${(p) => (p.$isExpandable ? '20px 36px' : '100px 200px')};

  > svg {
    height: 262px;
  }
`;

const StyledBigGraphWrapper = styled.div`
  border-radius: 12px;
  background-color: ${(p) => p.theme.greys.shade10};
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  height: 100%;
  width: 100%;
  text-align: center;

  &:after {
    content: '';
    position: absolute;
    right: 0;
    top: ${BIG_GRAPH_PADDING}px;
    height: calc(100% - ${BIG_GRAPH_PADDING * 2}px);
    width: ${BIG_GRAPH_PADDING}px;
    background: linear-gradient(-90deg, rgba(247, 248, 250, 0.71) 0%, rgba(247, 248, 250, 0) 100%);
  }

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: ${BIG_GRAPH_PADDING}px;
    height: calc(100% - ${BIG_GRAPH_PADDING * 2}px);
    width: ${BIG_GRAPH_PADDING}px;
    background: linear-gradient(90deg, rgba(247, 248, 250, 0.71) 0%, rgba(247, 248, 250, 0) 100%);
  }
`;

const StyledScrollableFrame = styled.div`
  height: 100%;
  width: 100%;
  overflow-x: auto;
`;

const StyledPaddedGraph = styled.div`
  height: 100%;
  padding: ${BIG_GRAPH_PADDING}px;
  width: fit-content;
  margin: auto;
`;

const StyledInboxName = styled.div`
  position: absolute;
  top: 20px;
  left: 22px;
  cursor: pointer;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  line-height: 21px;
`;

const StyledIconWrapper = styled.div<{$top: number; $right: number}>`
  position: absolute;
  top: ${(p) => p.$top}px;
  right: ${(p) => p.$right}px;
  cursor: pointer;
  padding: 4px;

  &:hover {
    border-radius: 6px;
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledDialog = styled(Dialog)`
  width: 90vw;
  height: 80vh;

  svg.graph {
    width: auto;
    height: 100%;
    display: inline-block;
  }
`;

/*
 * Components.
 */

/**
 * Display a graph representing all the inputs and outputs of an inbox. The graph can be zoomed in a modal.
 * @param inbox: Inbox represented in the graph.
 * @param channels: Channels feeding the inbox with conv.
 * @param moveInBundles: Bundles feeding the inbox with conv.
 * @param moveOutBundles: Bundles emptying the inbox of conv.
 */
export const ZoomableInboxGraph: FC<InboxGraphProps & {isExpandable: boolean}> = ({
  isExpandable,
  inbox,
  moveInBundles,
  moveOutBundles,
  ...inheritableProps
}) => {
  const [isModalDisplayed, toggleModalDisplayed] = useToggle(false);
  const scrollableFrameRef = useRef<HTMLDivElement>(null);
  const paddedGraphRef = useRef<HTMLDivElement>(null);
  const trackExpandEvent = useCallback(
    () => trackInterfaceEvent(InterfaceEventsEnum.INBOX_OVERVIEW_GRAPH_EXPAND),
    [],
  );
  const theme = useUserTheme();

  useLayoutEffect(() => {
    if (scrollableFrameRef.current && paddedGraphRef.current && isModalDisplayed) {
      scrollableFrameRef.current.scroll({
        left: (paddedGraphRef.current.clientWidth - scrollableFrameRef.current.clientWidth) / 2,
      });
    }
  }, [isModalDisplayed]);

  const graph = useMemo(
    () => (
      <InboxGraph
        inbox={inbox}
        moveInBundles={moveInBundles}
        moveOutBundles={moveOutBundles}
        {...inheritableProps}
      />
    ),
    [inbox, moveInBundles, moveOutBundles, inheritableProps],
  );

  // Used in the miniature.
  const smallGraph = (
    <StyledSmallGraphWrapper $isExpandable={isExpandable}>
      {isExpandable && (
        <StyledIconWrapper
          onClick={() => {
            trackExpandEvent();
            toggleModalDisplayed();
          }}
          $top={5}
          $right={5}
        >
          <Icon name="expandSmall" color={theme.greys.shade50} hoverColor={theme.greys.shade70} size={16} />
        </StyledIconWrapper>
      )}
      {graph}
    </StyledSmallGraphWrapper>
  );

  // Used in the modal.
  const bigGraph = (
    <StyledBigGraphWrapper>
      <StyledInboxName>{inbox.name}</StyledInboxName>
      <StyledIconWrapper onClick={toggleModalDisplayed} $top={19} $right={19}>
        <Icon name="collapseSmall" color={theme.greys.shade70} hoverColor={theme.greys.shade80} size={20} />
      </StyledIconWrapper>
      <StyledScrollableFrame ref={scrollableFrameRef}>
        <StyledPaddedGraph ref={paddedGraphRef}>{graph}</StyledPaddedGraph>
      </StyledScrollableFrame>
    </StyledBigGraphWrapper>
  );

  return (
    <>
      {smallGraph}
      {isModalDisplayed && <StyledDialog onRequestClose={toggleModalDisplayed}>{bigGraph}</StyledDialog>}
    </>
  );
};
